/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The wizard's form model, and the two acts that fill it from a document** (`TM-A`, `#1169`).
 *
 * Pulled out of the component for the reason `SimplifiedDemandSourceWizard.helpers.ts` was: this is
 * the whole of a judgement, the component around it cannot be render-tested cheaply, and the case
 * that was missing is invisible in the happy path. Editing a source and applying a template are the
 * same translation — *this artifact, as the form* — and they were two, which is how an ORTB
 * template's request body came to reach the wizard as nothing at all.
 *
 * Everything here is plain data in and plain data out. No JSX, no hooks, no services.
 */

import type {
  DemandSource,
  DemandSourceMethod,
  OrtbBodyTemplate,
  OrtbOutboundMapping,
  PrebidConfig,
} from '../../types';
import type { MappingDocument } from '../../types/mapping';
import { proposeArtifactName } from '../../utils/artifact-identity';
import {
  buildStarterTemplate,
  leavesToTemplate,
  pathToProvenanceKey,
  templateToLeaves,
  type MappingLeaf,
} from '../shared/editors/ortb-body-editor.adapters';
import {
  termsFormFromStored,
  dtoCustomPixelsToRows,
  headersObjectToRows,
  paramsDtoToRows,
  type TermsFormValue,
  type HeaderRow,
  type ParameterRow,
  type PixelRow,
} from '../shared';
import { extractProtocolVersion as readProtocolVersion } from '../shared';
import { isRequiredHere } from '@destination-field';
import { templateSuppliedOrigin, type ValueOrigin } from '@value-provenance';
import {
  isUneditedTemplateEndpoint,
  mergeTemplateItems,
  mergeTemplateRows,
} from './SimplifiedDemandSourceWizard.helpers.ts';

/** A single row in the Prebid bidder editor. */
export interface PrebidBidderRow {
  /** Unique row key for React rendering. */
  id: string;
  /** Prebid adapter name, e.g. 'appnexus', 'rubicon'. */
  name: string;
  /** Bidder-specific parameters, serialised as a JSON string for editing. */
  paramsJson: string;
}

export interface WizardForm {
  /** Retired form-field names, `never` for the reason given on `DemandSource`: a
   *  conditional spread bypasses excess-property checking, and both the URL-change
   *  handler and Quick-fill wrote `url`/`type` through one. */
  url?: never;
  type?: never;

  name: string;
  /**
   * The **authored** short-hand identifier (A7, `#1147`) — the tag referenced elsewhere, including
   * programmatically. Set once, frozen after creation, enforced unique.
   *
   * It used to be generated (`` `${slug(name)}-${Date.now()}-${random}` ``) and offered nowhere, so
   * nothing could reliably reference a source by it and every consumer accepted an id **or** a name.
   * That liberality is what A7 removes; this field is what removes it.
   */
  id: string;
  /**
   * Has the operator typed in the ID box? Once true the id stops following the name.
   *
   * Form state rather than a DTO field — `buildPayload` does not send it. An id that keeps
   * re-deriving from the name is an id that changes, which is the one thing an authored identifier
   * must never do.
   */
  idTouched?: boolean;
  method: DemandSourceMethod;
  /** Named for the DTO field it becomes. It was `url`, which is how `buildPayload`
   *  came to emit `url:` — see the note there. */
  endpoint: string;
  description: string;
  /**
   * Optional so the box can actually be **empty** while the operator retypes it.
   * Held as `number` it could not be: `Number('')` is `0`, which rendered back as a
   * `0` no backspace would remove. `NumberField` owns the draft text; the form holds
   * only what parsed, and the step validation below requires a value rather than a
   * default silently standing in for one.
   */
  timeout?: number;
  /** #1100 — declared, never inferred; absent means the optimistic default. */
  podMode?: 'pod' | 'single';
  podMaxImpressions?: number;
  /**
   * **Retired into {@link terms}** (slice `3c-i`): price, weight and max rounds are terms, and the
   * Terms box is exactly what resolves (`ST2`). `never`, as `url`/`type` are (`#936`), so a stale key
   * is a type error even through a spread.
   */
  weight?: never;
  floorCpm?: never;
  cpmIsFixed?: never;
  maxFetchRounds?: never;
  /**
   * Protocol version, stored at `DemandSourceDto.protocolVersion` for both paths (`#1135`).
   * VAST: '2.0'..'4.2' (default 4.0). OpenRTB: '2.5' | '2.6' (default 2.6) — drives
   * x-openrtb-version, the version-aware body catalog, and the placement field the
   * body carries (#1112 · D29). Not used for custom.
   */
  protocolVersion: string;
  enabled: boolean;
  owner: string;
  ownerId: string;
  visibility: 'public' | 'private';
  // Advanced fields
  headerRows: HeaderRow[];
  parameterRows: ParameterRow[];
  terms: TermsFormValue;
  /** Inbound response mapping (Universal Mapping Model, #866). */
  responseMapping?: MappingDocument;
  ortbOutboundMapping: OrtbOutboundMapping;
  ortbBodyTemplate?: OrtbBodyTemplate;
  pixelRows: PixelRow[];
  /** Pixel sampling rate as a percentage string (0–100); '' = 100% (no sampling). */
  pixelSamplingRate: string;
  // Prebid Server configuration (OpenRTB only)
  prebidEnabled: boolean;
  prebidStoredRequestId: string;
  prebidStoredImpId: string;
  prebidBidderRows: PrebidBidderRow[];
}

export const DEFAULT_ORTB_OUTBOUND_MAPPING: OrtbOutboundMapping = {};

export const DEFAULT_FORM: WizardForm = {
  name: '',
  id: '',
  method: 'VAST',
  endpoint: '',
  description: '',
  timeout: 2000,
  // Absent, not `'pod'`. Seeding the optimistic default as an authored value would make
  // every source claim a declaration nobody made — and the whole point of #1100 is that
  // this field means an operator said so (#959).
  podMode: undefined,
  protocolVersion: '4.0',
  enabled: true,
  owner: '',
  ownerId: '',
  visibility: 'private',
  headerRows: [],
  parameterRows: [],
  terms: termsFormFromStored(undefined, null),
  responseMapping: undefined,
  ortbOutboundMapping: { ...DEFAULT_ORTB_OUTBOUND_MAPPING },
  ortbBodyTemplate: undefined,
  pixelRows: [],
  pixelSamplingRate: '',
  prebidEnabled: false,
  prebidStoredRequestId: '',
  prebidStoredImpId: '',
  prebidBidderRows: [],
};

/**
 * Protocol version is a parity-required feature (VAST & OpenRTB): both drivers read
 * it from `DemandSourceDto.protocolVersion` (driver config, never a request parameter — `#1135`).
 * VAST uses it as the declared VAST version (default 4.0); OpenRTB reads it for the
 * `x-openrtb-version` header, the version-aware body catalog, **and since #1112 the
 * placement field the request body actually carries** — `video.plcmt` on 2.6,
 * `video.placement` on 2.5, never both (`CUSTOM_FIELDS_DESIGN.md` D29). Default 2.6.
 *
 * That last one raised the bar on this control: it used to decide a header and an
 * editor's weighting, and now decides a field an exchange reads. Getting it wrong is
 * no longer cosmetic.
 * Newest-first options; `hasSelector` gates the Connection-step control (custom has none).
 */
export const PROTOCOL_VERSIONS: Record<
  DemandSourceMethod,
  { default: string; options: string[]; hasSelector: boolean }
> = {
  VAST: { default: '4.0', options: ['4.2', '4.1', '4.0', '3.0', '2.0'], hasSelector: true },
  ORTB: { default: '2.6', options: ['2.6', '2.5'], hasSelector: true },
  API: { default: '', options: [], hasSelector: false },
};

/** The stored protocol version, else this type's default. Reader lives in the params adapter. */
export function extractProtocolVersion(
  source: { protocolVersion?: unknown } | null | undefined,
  type: DemandSourceMethod,
): string {
  return readProtocolVersion(source, PROTOCOL_VERSIONS[type].default);
}

/**
 * Canonicalise a stored `method` for the form.
 *
 * `METHOD_TO_TYPE` used to live here, translating the backend's `'VAST'|'ORTB'|'API'`
 * into the console's lowercase set. Since #936 both sides use the same values, so it
 * became an identity map and was deleted. This remains only to tolerate historical
 * spellings on records written before the write boundary normalised them — the same
 * job `normalizeDemandSourceMethod` does in libs/dto.
 */
export function toMethod(raw?: string | null): DemandSourceMethod {
  const normalized = String(raw ?? '')
    .trim()
    .toUpperCase();
  if (normalized === 'ORTB' || normalized === 'OPENRTB' || normalized === 'OPEN-RTB') return 'ORTB';
  if (normalized === 'API' || normalized === 'CUSTOM') return 'API';
  return 'VAST';
}

/**
 * Give a source that just became OpenRTB a starting body — the merge-mode
 * starter (privacy + supply chain), i.e. the fields the driver cannot derive
 * from the session. Never overwrites a template that already has fields.
 */
export function seedOrtbBodyTemplate(
  existing: OrtbBodyTemplate | undefined,
  version: string | null,
): OrtbBodyTemplate | undefined {
  if (existing && Object.keys(existing.body ?? {}).length > 0) return existing;
  return buildStarterTemplate('merge', version);
}

/** Convert a PrebidConfig object from the backend into editable bidder rows. */
export function prebidConfigToBidderRows(cfg: PrebidConfig | null | undefined): PrebidBidderRow[] {
  if (!cfg?.bidders) return [];
  return Object.entries(cfg.bidders).map(([name, params], idx) => ({
    id: `bidder-${idx}-${Date.now()}`,
    name,
    paramsJson: JSON.stringify(params, null, 2),
  }));
}

/**
 * A stored demand source → the wizard's form (`TM-A`, `#1169`).
 *
 * **One hydration, for both acts that open a document in this wizard.** Editing a source and
 * applying a template are the same translation — *this artifact, as the form* — and they were two:
 * edit read the whole document inline here, while apply read four fields off `AdServerPreset`. So a
 * template carrying an ORTB body, a response mapping, terms or pixels lost all of it on the way in,
 * silently, and the operator read that as *templates do not work for OpenRTB* (`#1169` §2.4:
 * 55 body leaves and 38 constants dropped from one real customer template).
 *
 * That is CLAUDE.md's enumerated-columns rule occurring for a **document** rather than a column set,
 * and the fix is the same one: stop enumerating. A field added to `DemandSource` now reaches both
 * acts for free, and there is no list to forget to extend.
 *
 * **It carries content and never identity.** `TM3`'s `copy = payload − ArtifactBaseDto` is the
 * caller's job, not this function's: `formFromDemandSource` is faithful to the document it is given,
 * and {@link applyTemplateToForm} is what decides which of those values a *new* artifact may keep.
 */
/**
 * **The Prebid config a form states**, or `null` for *off* (`3f`). One projection for the wizard and the
 * detail page's in-place Prebid section, so the two cannot come to store a different config for the
 * same form. Throws on bidder parameters that are not JSON; a caller refuses that before saving.
 *
 * `null` rather than `undefined` for *off*: an update merges over what is stored, so leaving the key
 * out keeps a config the operator just switched off.
 */
export function prebidConfigFromForm(form: WizardForm): PrebidConfig | null {
  if (form.method !== 'ORTB' || !form.prebidEnabled) return null;
  const bidders: Record<string, Record<string, unknown>> = {};
  for (const row of form.prebidBidderRows) {
    const name = row.name.trim();
    if (!name) continue;
    bidders[name] = JSON.parse(row.paramsJson || '{}') as Record<string, unknown>;
  }
  const config: PrebidConfig = {
    ...(Object.keys(bidders).length > 0 ? { bidders } : {}),
    ...(form.prebidStoredRequestId.trim() ? { storedRequestId: form.prebidStoredRequestId.trim() } : {}),
    ...(form.prebidStoredImpId.trim() ? { storedImpId: form.prebidStoredImpId.trim() } : {}),
  };
  // Enabled with nothing in it states nothing a driver would send.
  return Object.keys(config).length > 0 ? config : null;
}

export function formFromDemandSource(source: DemandSource): WizardForm {
  const legacyParams = (source as DemandSource & { params?: Record<string, unknown> }).params;
  // Respect the stored parameter set exactly — including an empty one. Seeding
  // STANDARD_FIELD_DEFAULTS here made a deliberately-CLEARED param set look like
  // it "reverted" on reopen (the deletion persisted, but the editor re-populated
  // the 12 defaults), and — with the save path now serializing whatever the
  // editor shows — could even re-persist those defaults. Operators add the
  // standard fields on demand via the editor's "+ Add Missing Fields" button.
  // **The operator claim comes off the side-car, for every plane** (`TM4`, `#1170`). It used to
  // ride in `params.fields`, which reached one destination of one type and left an ORTB body leaf
  // unable to say the same thing.
  // No read-back for parameters any more: `optional` is the claim, and it has always lived on the
  // row itself. The side-car only carries the plane whose row cannot hold it (`P1` slice 3).
  const parameterRows = paramsDtoToRows(source.params ?? legacyParams);
  const fallbackUrl = source.endpoint ?? (source as DemandSource & { endpoint?: string }).endpoint ?? '';
  return {
    name: source.name ?? '',
    id: String(source.id ?? ''),
    method: toMethod(source.method),
    endpoint: fallbackUrl,
    description: source.description ?? '',
    // **Absent stays absent** (`3h`). It loaded as 2000 — while the driver's default is 3000
    // (`BaseDriver.DEFAULT_REQUEST_TIMEOUT_MS`) — so the editor claimed a value nothing was using, and
    // saving any Connection edit wrote it and expired the proof. An empty box now says what is true,
    // and its finding says the driver's own applies (`formFieldFindings`).
    timeout: source.timeout,
    podMode: source.podCapability?.mode,
    podMaxImpressions: source.podCapability?.maxImpressions,
    protocolVersion: extractProtocolVersion(source, toMethod(source.method)),
    enabled: source.enabled ?? true,
    // The owner's label, never the author's (`#900`): a transferred source used to show its creator.
    owner: source.ownerId ? (source.ownerName ?? source.ownerId) : 'System',
    ownerId: source.ownerId ?? '',
    visibility: source.visibility ?? (source.ownerId ? 'private' : 'public'),
    headerRows: headersObjectToRows(source.headers, source.headersProvenance),
    parameterRows,
    terms: termsFormFromStored(source.terms, source.restrictions ?? null),
    responseMapping: source.responseMapping,
    ortbOutboundMapping: source.ortbOutboundMapping
      ? { ...DEFAULT_ORTB_OUTBOUND_MAPPING, ...source.ortbOutboundMapping }
      : { ...DEFAULT_ORTB_OUTBOUND_MAPPING },
    ortbBodyTemplate: source.ortbBodyTemplate,
    pixelRows: dtoCustomPixelsToRows(source.customPixels),
    pixelSamplingRate:
      source.pixelSamplingRate != null && source.pixelSamplingRate < 1
        ? String(Math.round(source.pixelSamplingRate * 100))
        : '',
    prebidEnabled: !!source.prebidConfig,
    prebidStoredRequestId: source.prebidConfig?.storedRequestId ?? '',
    prebidStoredImpId: source.prebidConfig?.storedImpId ?? '',
    prebidBidderRows: prebidConfigToBidderRows(source.prebidConfig),
  };
}

/**
 * **What a template contributes to a form, and what it must never touch** (`TM3`, `#1169`).
 *
 * Declared as a total `Record<keyof WizardForm, …>` on purpose: the failure this slice exists to fix
 * is *an enumerated list of the fields that matter*, so the list that replaces it is one a **new
 * form field cannot be missing from** — `tsc -b` fails until it is classified. That is the
 * corollary's test made mechanical (*is the second case an addition to this design, or a change to
 * it?*), and it lives in source rather than in a spec because `tsconfig.build.json` excludes specs
 * from emit, so a guard written there fires nowhere else.
 *
 * | | |
 * | --- | --- |
 * | `identity` | the artifact's own, never a template's to give — `TM3`'s `copy = payload − ArtifactBaseDto` |
 * | `content`  | travels, subject to the withdraw-on-switch rule below |
 * | `collection` | travels **element-wise** — merged by {@link mergeTemplateItems}, not compared whole |
 *
 * `name` is deliberately `content`. It is not identity — `id` is — and seeding an empty name box
 * from the template is the behaviour that already existed; routing it through the general rule adds
 * the half that was missing, so switching template also replaces a name the previous one supplied.
 */
type TemplateFieldPolicy = 'identity' | 'content' | 'collection';

const TEMPLATE_FIELD_POLICY: Record<keyof WizardForm, TemplateFieldPolicy> = {
  // Retired wire names, kept as `never` so a stale key is a type error (#936). Nothing to carry.
  url: 'identity',
  type: 'identity',
  // The artifact's own identity, ownership and lifecycle.
  id: 'identity',
  idTouched: 'identity',
  owner: 'identity',
  ownerId: 'identity',
  visibility: 'identity',
  enabled: 'identity',
  // Content.
  name: 'content',
  method: 'content',
  endpoint: 'content',
  description: 'content',
  timeout: 'content',
  podMode: 'content',
  podMaxImpressions: 'content',
  // Retired into `terms` (`3c-i`), kept as `never`. Nothing to carry.
  weight: 'identity',
  floorCpm: 'identity',
  cpmIsFixed: 'identity',
  maxFetchRounds: 'identity',
  protocolVersion: 'content',
  terms: 'content',
  responseMapping: 'content',
  ortbOutboundMapping: 'content',
  pixelSamplingRate: 'content',
  prebidEnabled: 'content',
  prebidStoredRequestId: 'content',
  prebidStoredImpId: 'content',
  // Addressable collections a template contributes to — merged per slot, never wholesale.
  parameterRows: 'collection',
  headerRows: 'collection',
  ortbBodyTemplate: 'collection',
  pixelRows: 'collection',
  prebidBidderRows: 'collection',
};

/** Structural equality over plain form data. Every `content` value is JSON-shaped by construction. */
function sameFormValue(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** A header row's slot. */
const headerSlot = (row: HeaderRow): string => row.key.trim().toLowerCase();

/** Unchanged since a template put it there — the header equivalent of a row's value comparison. */
const sameHeaderValue = (a: HeaderRow, b: HeaderRow): boolean =>
  (a.value ?? '') === (b.value ?? '') && sameFormValue(a.expression, b.expression);

/** Unchanged since a template put it there — for one ORTB body leaf. */
const sameLeafValue = (a: MappingLeaf, b: MappingLeaf): boolean =>
  a.kind === b.kind &&
  (a.asType ?? '') === (b.asType ?? '') &&
  sameFormValue(a.staticValue, b.staticValue) &&
  sameFormValue(a.expression, b.expression);

/**
 * **The ORTB body, under the same withdraw-on-switch rule the parameter rows have had since
 * `#1115`** — which is the half `#1169` is named for.
 *
 * A body leaf is addressed by its path, so `pathToProvenanceKey` is the slot: the same key the
 * provenance side-car already uses, so a leaf and its `template` mark cannot disagree about which
 * leaf they describe.
 *
 * `mode` follows the incoming template's, because `merge` and `replace` are a statement about the
 * document as a whole rather than about any leaf — a `replace` template merged into a `merge` body
 * would send a document neither side authored.
 */
function mergeTemplateBody(
  current: OrtbBodyTemplate | undefined,
  previouslySeeded: OrtbBodyTemplate | undefined,
  nowSeeded: OrtbBodyTemplate | undefined,
): OrtbBodyTemplate | undefined {
  const leaves = mergeTemplateItems(
    templateToLeaves(current),
    templateToLeaves(previouslySeeded).map(asTemplateSuppliedRow),
    templateToLeaves(nowSeeded).map(asTemplateSuppliedRow),
    (leaf) => pathToProvenanceKey(leaf.path),
    sameLeafValue,
  );
  if (leaves.length === 0) return undefined;
  return leavesToTemplate(leaves, nowSeeded?.mode ?? current?.mode ?? 'merge');
}

/**
 * **A row the template declares and `applyTemplateToForm` deliberately does not seed** — optional,
 * with nothing in it.
 *
 * A value in an optional row means the template chose it — a vendor constant, a default the operator
 * would usually keep — so it seeds like anything else. Emptiness is the declaration.
 *
 * **This is the seeding question only** (narrowed 2026-08-26). It used to serve the picker as well,
 * on the argument that the withheld set and the offerable set are complements — and they are not:
 * *what may be added later* includes anything the source does not currently have, which is why
 * deleting a populated row put it out of reach. See {@link templateSuppliedRows}.
 */
export function isWithheldAtApply(row: ParameterRow): boolean {
  // **Answered, not merely literal** (`N11f`). The predicate read `value` alone, which was the
  // right question only while `value` also held the macro token: after the split a `{cb}` row
  // has an empty `value` and a full answer, so it would have been withheld as *yours to fill*
  // — a template offering the operator a field it had already mapped.
  const answered = String(row.value ?? '').trim() !== '' || !!row.expression;
  // **Required-ness, not the omission answer** (`X46`, 2026-09-02). This read `row.optional`, which
  // is the *wire* answer — omit rather than send `foo=` — and the question here is whether the
  // destination needs a value. They were one boolean until the split, so this was right by
  // coincidence; it is now right by construction, and the row it would have got wrong is the one
  // that matters most: a field an operator declared **required** while choosing to omit it when
  // unresolved would have been withheld from the source it was applied to.
  //
  // Behaviour on every stored row is unchanged: `optional: false` reads back as `required` and
  // absent reads back as nothing stated, so the two predicates agree everywhere except on the
  // combination that was previously unrepresentable.
  return !isRequiredHere(row.importance) && !answered;
}

/**
 * **What a template declares** (`#1166`) — the rows the picker can offer, whatever the form
 * currently holds.
 *
 * **Widened 2026-08-26, from validation:** *"deleting a populated field doesn't offer it as optional
 * again."* Right, and the reason is that this returned only what the template **withheld**, so a row
 * the template had filled in was never in the offerable set and deleting it put it out of reach.
 *
 * The picker's question is not *what did this template withhold* — it is **what does this template
 * have that this source does not**, which is a function of two things and cannot be answered by the
 * template alone. `RequestParameterEditor` already asks the second half (`!existingNames.has(...)`),
 * so the set handed to it is simply everything the template declares.
 *
 * That retires the *"one predicate, no gap and no overlap"* partition this and `applyTemplateToForm`
 * used to share. The partition was the defect, not the property: `isWithheldAtApply` is still
 * exactly right for **what to seed at apply** — pre-adding an empty optional row greeted every new
 * source with warnings about fields that did not matter — and was never the right question for
 * *what may be added later*.
 *
 * Withholding them at apply is right (`applyTemplateToForm`): pre-adding greeted every new source
 * with a warnings panel about fields that did not matter. But a value withheld with **no way to
 * reach it** is the other half of the same defect, and that is what shipped — `iu`, `cust_params`,
 * `pubid`, `inventory_route_id` are in no catalog, in no *Sent automatically* panel and in no
 * autocomplete, so the only route to one was typing the exact name out of the vendor's PDF.
 *
 * The rows are returned **verbatim**, not projected into a name and a description: adding one must
 * reproduce what the template authored — its mapping, its `sendAs`, its `suppliedBy` coverage claim
 * — and a projection that narrows content here is `TM2`'s defect at row scale. The one thing that
 * *is* restated is provenance, because that is a fact about **this** artifact rather than content;
 * see {@link asTemplateSuppliedRow}.
 */
export function templateSuppliedRows(template: DemandSource | undefined): ParameterRow[] {
  if (!template) return [];
  return formFromDemandSource(template).parameterRows.map(asTemplateSuppliedRow);
}

/**
 * **A row the template supplies, marked as such** (`R4`/`R4b`; found in validation 2026-08-26).
 *
 * Provenance answers *how did this row come to be **in this artifact***, and a row that arrived
 * from a template came from a template — whatever the template's own history says about it.
 *
 * A template **saved from a demand source** carries that source's provenance verbatim, so every row
 * of `Proxy VAST Template` says `detected`: true of the source it was cloned from, and false of the
 * new source it seeds. Reported as *"fields from template show up as `detected` — which they were in
 * the source the template was created from."* Shipped templates carry no provenance at all, which is
 * why they read correctly and hid this: the defect is only reachable once an operator makes a
 * template out of real work, which is the gesture `TM-C` shipped the day before.
 *
 * **The stamp is not new — it was lost.** `rowFromCatalogField` marks its rows `template` and
 * `#1090`'s preset path did the same; `TM-A` (`#1169`) replaced that projection with the artifact
 * itself, correctly, and the stamp went with it. That is `TM2` one row down: a consumer that reads
 * the artifact must still say **how the value got here**, because the document cannot.
 */
function asTemplateSuppliedRow<Row extends { provenance?: ValueOrigin }>(row: Row): Row {
  // A plain `template` origin, never the template's own record: a detected guess on the source the
  // template was saved from is not a guess about **this** source, and it has nothing to revert to here.
  //
  // **Every surface a template seeds, not only parameters** (2026-09-24). Headers and ORTB body leaves
  // kept the template's own provenance, so a header the template's author wrote by hand arrived
  // unchipped while one the catalog seeded inside the template said `from template`: one chip meaning
  // two things on neighbouring surfaces, and nothing to mark `overridden` when the operator changed
  // the unchipped one.
  //
  // **Its grade travels** (`D40`, `1f-iii`): *inferred* is a claim about the vendor's field, true on any
  // source made from the template, so `templateSuppliedOrigin` keeps it and drops everything else.
  return { ...row, provenance: templateSuppliedOrigin(row.provenance) };
}

/**
 * **Applying a template is hydrating its document into the form, minus the artifact's identity**
 * (`TM-A`, `#1169`; `TM3`).
 *
 * This replaces a merge written against `AdServerPreset`'s four content fields, which is why an
 * ORTB template's body, response mapping, terms and pixels reached the wizard as nothing at all.
 * There is no field list here any more: {@link formFromDemandSource} produces the whole document as
 * a form, and {@link TEMPLATE_FIELD_POLICY} says only *which plane* each key is on.
 *
 * **The rule for a `content` field is `#1115`'s, one level up.** A value the operator has not
 * diverged from — still exactly what the template they are leaving put there, or still the form's
 * own default if this is the first template applied — is the template's to replace. Anything they
 * typed is theirs and survives. That makes *switch template* symmetric with *switch template back*,
 * which the old `prev.x || template.x` guards could not be: they filled an empty field once and
 * then refused to move for the rest of the session.
 *
 * **Empty optional rows are not seeded, and that is a policy about applying rather than a loss.**
 * A required parameter with no value is the checklist item the operator needs told about; an
 * optional one with nothing in it changes nothing if left alone, and pre-adding it greeted every
 * new source with *"has no value and won't be sent"* for a field that did not matter. That is how a
 * warnings panel gets trained out of usefulness.
 *
 * **The name is the one `content` field that is transformed rather than copied** (`ST12` item 3,
 * ruled by Todd 2026-09-22). This is the *template → source* direction, so the word `Template` comes
 * **off**: a template called `SpringServe Template` proposes a source called `SpringServe`, where it
 * used to propose `SpringServe Template` and leave the operator to delete a word on every source
 * they ever created from one. It is the template's name that is the suggestion, so the rule applies
 * where the suggestion is made — see `proposeArtifactName`.
 */
export function applyTemplateToForm(
  prev: WizardForm,
  template: DemandSource,
  previousTemplate: DemandSource | undefined,
  knownTemplates: readonly DemandSource[],
  /**
   * The names this viewer can see, so the proposed name does not collide with one of them.
   *
   * Required and nullable (`#1134`): `null` is a caller with no list. It is a suggestion input and
   * never a verdict — the same statement `CloneArtifactDialogProps.existingNames` carries.
   */
  takenNames: readonly string[] | null,
): WizardForm {
  const next = formFromDemandSource(template);
  const before = previousTemplate ? formFromDemandSource(previousTemplate) : DEFAULT_FORM;

  const merged: WizardForm = { ...prev };
  for (const key of Object.keys(TEMPLATE_FIELD_POLICY) as (keyof WizardForm)[]) {
    if (TEMPLATE_FIELD_POLICY[key] !== 'content') continue;
    if (!sameFormValue(prev[key], before[key])) continue;
    (merged as unknown as Record<string, unknown>)[key] = next[key];
  }

  // Applied **after** the loop rather than instead of it, so *has the operator diverged from the
  // template they are leaving* is still asked of the template's own name — the question `#1115`
  // makes switching symmetric. What changes is only what the answer proposes.
  if (merged.name === next.name) {
    merged.name = proposeArtifactName({
      sourceName: template.name ?? '',
      sourceIsTemplate: true,
      // Applying a template is how a **source** is created. An operator authoring a template from
      // another one reaches it through the clone gesture, which asks its own question.
      targetIsTemplate: false,
      // Not a copy: `fromTemplateId` records where it came from and nothing tracks the template
      // (`TM9`), so there is no relationship for a suffix to name.
      relation: null,
      taken: takenNames ?? [],
    });
  }

  const seedable = (rows: ParameterRow[]): ParameterRow[] => rows.filter((row) => !isWithheldAtApply(row));

  merged.parameterRows = mergeTemplateRows(
    prev.parameterRows,
    seedable(before.parameterRows.map(asTemplateSuppliedRow)),
    seedable(next.parameterRows.map(asTemplateSuppliedRow)),
  );
  merged.headerRows = mergeTemplateItems(
    prev.headerRows,
    before.headerRows.map(asTemplateSuppliedRow),
    next.headerRows.map(asTemplateSuppliedRow),
    headerSlot,
    sameHeaderValue,
  );
  merged.ortbBodyTemplate = mergeTemplateBody(prev.ortbBodyTemplate, before.ortbBodyTemplate, next.ortbBodyTemplate);
  // Pixels and Prebid bidders carry generated row ids, so *unchanged since the template* is not a
  // question that can be asked of them element-wise. The template's set is taken only where the
  // operator holds none — the conservative half of the same rule, and the one that cannot destroy
  // work.
  if (prev.pixelRows.length === 0) merged.pixelRows = next.pixelRows;
  if (prev.prebidBidderRows.length === 0) merged.prebidBidderRows = next.prebidBidderRows;

  /**
   * The endpoint keeps its own test, because a *shape* is not a value (`#955`).
   *
   * `isUneditedTemplateEndpoint` asks whether what is in the box still matches **any** template's
   * endpoint, which is a wider question than *the one you are leaving* — a Quick-filled endpoint
   * detected from a pasted tag was put there by a template the operator never selected.
   */
  merged.endpoint = isUneditedTemplateEndpoint(prev.endpoint, knownTemplates) ? next.endpoint : prev.endpoint;

  // An ORTB source with no body at all is unauthored rather than deliberate: the starter is what
  // gives the operator something to edit. A template that carries one has already answered this.
  if (merged.method === 'ORTB') {
    merged.ortbBodyTemplate = seedOrtbBodyTemplate(merged.ortbBodyTemplate, merged.protocolVersion || null);
  }

  return merged;
}
